import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../components/ui/Button";
import Loader from "../../components/ui/Loader";
import { useToast } from "../../contexts/ToastContext";
import { useGetProfileQuery } from "../../store/api/authApi";
import {
	useCompleteMaintenanceRequestMutation,
	useListMaintenanceRequestsQuery,
	useUpdateMaintenanceRequestMutation,
} from "../../store/api/maintenanceApi";
import type { MaintenanceRequest, MaintenanceStatus } from "../../types";
import { formatDateTime } from "../../utils/formatDate";
import ActiveTasksList from "./components/ActiveTasksList";
import CommunicationPanel from "./components/CommunicationPanel";
import ScheduleView from "./components/ScheduleView";
import TaskDetailPanel from "./components/TaskDetailPanel";
import TaskFilters from "./components/TaskFilters";
import TaskStatsCard from "./components/TaskStatsCard";

const TechnicianDashboard = () => {
	const navigate = useNavigate();
	const { success, error: showError } = useToast();
	const [selectedView, setSelectedView] = useState("tasks"); // tasks, schedule
	const [selectedTask, setSelectedTask] = useState<MaintenanceRequest | null>(
		null,
	);
	const [filterStatus, setFilterStatus] = useState("all");
	const [filterPriority, setFilterPriority] = useState("all");
	const [currentTime, setCurrentTime] = useState(new Date());

	// Get current user profile
	const { data: userProfile } = useGetProfileQuery();
	// const technicianId = userProfile?.id || ""; // TODO: Use for filtering when assigned_to is added to API

	// Fetch maintenance requests assigned to this technician
	// Enable polling every 60 seconds for real-time updates
	const {
		data: maintenanceData,
		isLoading: isLoadingTasks,
		refetch: refetchTasks,
	} = useListMaintenanceRequestsQuery(
		{
			page_size: 1000, // Get all tasks
		},
		{
			pollingInterval: 60000, // Poll every 60 seconds for updates
			refetchOnMountOrArgChange: true,
		},
	);

	// Backend already filters by assigned technician for technicians
	// But we'll keep this as a safety check
	const technicianTasks = useMemo(() => {
		if (!maintenanceData?.results || !userProfile?.id) return maintenanceData;
		// Backend should already filter, but double-check on frontend
		return {
			...maintenanceData,
			results: maintenanceData.results.filter(
				(req) => req.assigned_to_id === userProfile.id,
			),
		};
	}, [maintenanceData, userProfile]);

	// Mutations
	const [updateMaintenance] = useUpdateMaintenanceRequestMutation();
	const [completeMaintenance] = useCompleteMaintenanceRequestMutation();

	// Update current time every minute
	useEffect(() => {
		const timer = setInterval(() => {
			setCurrentTime(new Date());
		}, 60000);
		return () => clearInterval(timer);
	}, []);

	// Transform maintenance requests to tasks format
	const tasks = useMemo((): MaintenanceRequest[] => {
		if (!technicianTasks?.results) return [];
		return technicianTasks.results.map((req): MaintenanceRequest => {
			// Format dates using centralized formatDateTime
			const scheduledDateTime = req.scheduled_date
				? formatDateTime(req.scheduled_date)
				: "";
			const scheduledDate = scheduledDateTime.split(" ")[0] || "";
			const scheduledTime =
				scheduledDateTime.split(" ").slice(1).join(" ") || "";

			return {
				id: req.id,
				propertyId: req.property_id,
				property_name: req.property_name,
				property_address: req.location || "",
				location: req.location || "",
				title: req.title,
				description: req.description,
				status: (req.status === "in_progress" ? "in-progress" : req.status) as MaintenanceStatus,
				priority: req.priority as any,
				category: req.priority, // Using priority as category for now
				requested_by_name: req.requested_by_name,
				estimatedCost: req.estimated_cost,
				estimatedTime: "N/A", // Not in backend model yet
				actualCost: req.actual_cost,
				images: [],
				photo_urls: req.photo_urls || [],
				createdAt: new Date(req.created_at),
				created_at: req.created_at,
				updatedAt: new Date(req.updated_at),
				updated_at: req.updated_at,
				scheduledDate: scheduledDate as any,
				scheduled_date: req.scheduled_date,
				scheduledTime: scheduledTime,
				completedAt: req.completed_date ? new Date(req.completed_date) : undefined,
				completed_date: req.completed_date,
				notes: req.notes || "",
				time_spent: typeof req.time_spent === 'string' ? parseFloat(req.time_spent) : req.time_spent,
				// Additional fields for components
				unit: req.location || "",
				tenantContact: {
					name: req.requested_by_name || "Unknown",
					phone: req.requested_by_phone || "",
					email: req.requested_by_email || "",
				},
			};
		});
	}, [technicianTasks]);

	// Filter tasks based on selected filters
	const filteredTasks = useMemo(() => {
		return tasks.filter((task) => {
			const matchesStatus =
				filterStatus === "all" || task.status === filterStatus;
			const matchesPriority =
				filterPriority === "all" || task.priority === filterPriority;
			return matchesStatus && matchesPriority;
		});
	}, [tasks, filterStatus, filterPriority]);

	// Task statistics
	const taskStats = useMemo(() => {
		return {
			total: tasks.length,
			assigned: tasks.filter((t) => t.status === "pending").length,
			inProgress: tasks.filter(
				(t) => t.status === "in-progress" || t.status === "in_progress",
			).length,
			completed: tasks.filter((t) => t.status === "completed").length,
			followUp: tasks.filter(
				(t) => t.status === "on_hold" || t.status === "on-hold",
			).length,
		};
	}, [tasks]);

	// Technician info from profile
	const technicianInfo = useMemo(() => {
		if (!userProfile)
			return { name: "Technician", specialties: [], completedTasks: 0 };
		return {
			id: userProfile.id,
			name:
				`${userProfile.first_name} ${userProfile.last_name}`.trim() ||
				"Technician",
			specialties: userProfile.primary_specialty
				? [userProfile.primary_specialty]
				: [],
			completedTasks: taskStats.completed,
			rating: 0, // Not in user model yet
			certifications: [], // Not in user model yet
		};
	}, [userProfile, taskStats.completed]);

	const handleTaskSelect = (task: MaintenanceRequest): void => {
		setSelectedTask(task);
	};

	const handleTaskUpdate = async (taskId: string, updates: any) => {
		try {
			await updateMaintenance({
				id: taskId,
				data: updates,
			}).unwrap();
			refetchTasks();
			success("Task updated successfully!");
		} catch (error: any) {
			console.error("Failed to update task:", error);
			const errorMessage =
				error?.data?.detail ||
				error?.data?.message ||
				"Failed to update task. Please try again.";
			showError(errorMessage);
		}
	};

	const handleStatusUpdate = async (
		taskId: string,
		newStatus: string,
		notes: string = "",
	) => {
		try {
			if (newStatus === "completed") {
				await completeMaintenance({
					id: taskId,
					data: {
						resolution_notes: notes,
					},
				}).unwrap();
				success("Task marked as completed!");
			} else {
				const statusValue =
					newStatus === "in-progress" ? "in_progress" : newStatus;
				await updateMaintenance({
					id: taskId,
					data: {
						status: statusValue as any,
						notes: notes,
					},
				}).unwrap();
				success(`Task status updated to ${newStatus.replace("-", " ")}`);
			}
			refetchTasks();
		} catch (error: any) {
			console.error("Failed to update task status:", error);
			const errorMessage =
				error?.data?.detail ||
				error?.data?.message ||
				"Failed to update task status. Please try again.";
			showError(errorMessage);
		}
	};

	// Loading state
	if (isLoadingTasks) {
		return (
			<Loader size="lg" showText text="Loading tasks..." className="h-64" />
		);
	}

	return (
		<div className="w-full">
			<div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
				{/* Page Header */}
				<div className="mb-6">
					<div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
						<div>
							<h1 className="text-2xl font-bold text-foreground">
								Technician Dashboard
							</h1>
							<p className="text-muted-foreground mt-1">
								Welcome back, {technicianInfo.name}! You have{" "}
								{taskStats.assigned} assigned tasks today.
							</p>
						</div>
						<div className="flex items-center space-x-3 mt-4 lg:mt-0">
							<div className="text-sm text-muted-foreground">
								{currentTime.toLocaleString("en-US", {
									weekday: "short",
									month: "short",
									day: "numeric",
									hour: "2-digit",
									minute: "2-digit",
								})}
							</div>
							<Button
								variant="outline"
								iconName="MessageSquare"
								iconPosition="left"
								onClick={() => navigate("/messages")}
							>
								Messages
							</Button>
						</div>
					</div>
				</div>

				{/* Task Statistics */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
					<TaskStatsCard
						title="Total Tasks"
						value={taskStats.total}
						icon="ClipboardList"
						color="primary"
					/>
					<TaskStatsCard
						title="Assigned"
						value={taskStats.assigned}
						icon="Clock"
						color="warning"
					/>
					<TaskStatsCard
						title="In Progress"
						value={taskStats.inProgress}
						icon="PlayCircle"
						color="primary"
					/>
					<TaskStatsCard
						title="Completed"
						value={taskStats.completed}
						icon="CheckCircle"
						color="success"
					/>
					<TaskStatsCard
						title="Follow-up"
						value={taskStats.followUp}
						icon="AlertCircle"
						color="error"
					/>
				</div>

				{/* View Toggle */}
				<div className="flex items-center space-x-4 mb-6">
					<div className="flex bg-muted rounded-lg p-1">
						<button
							onClick={() => setSelectedView("tasks")}
							className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
								selectedView === "tasks"
									? "bg-background text-foreground shadow-sm"
									: "text-muted-foreground hover:text-foreground"
							}`}
						>
							Active Tasks
						</button>
						<button
							onClick={() => setSelectedView("schedule")}
							className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
								selectedView === "schedule"
									? "bg-background text-foreground shadow-sm"
									: "text-muted-foreground hover:text-foreground"
							}`}
						>
							Schedule
						</button>
					</div>
				</div>

				<div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
					{/* Main Content Area */}
					<div className="xl:col-span-8">
						{selectedView === "tasks" && (
							<div className="space-y-6">
								<TaskFilters
									filterStatus={filterStatus}
									setFilterStatus={setFilterStatus}
									filterPriority={filterPriority}
									setFilterPriority={setFilterPriority}
								/>
								<ActiveTasksList
									tasks={filteredTasks}
									onTaskSelect={handleTaskSelect}
									onStatusUpdate={handleStatusUpdate}
									selectedTask={selectedTask}
								/>
							</div>
						)}

						{selectedView === "schedule" && (
							<ScheduleView tasks={tasks} onTaskSelect={handleTaskSelect} />
						)}
					</div>

					{/* Right Sidebar */}
					<div className="xl:col-span-4 space-y-6">
						{selectedTask && (
							<TaskDetailPanel
								task={selectedTask}
								onTaskUpdate={handleTaskUpdate}
								onStatusUpdate={handleStatusUpdate}
							/>
						)}
						<CommunicationPanel />
					</div>
				</div>
			</div>
		</div>
	);
};

export default TechnicianDashboard;
